import classNames from 'classnames';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  CATEGORIES,
  FontAwesomeIcon,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { Loader } from '@hivebrite/design-system/Loader';
import { type ColorName } from '@hivebrite/design-system/Theme';
import {
  focusOutline,
  responsive,
  useTheme,
} from '@hivebrite/design-system/Theme';
import { usePrevious } from '@hivebrite/react-hooks';

import { LocationHiddenInputs } from 'hb-react/shared/components/DesignSystem/Inputs/InputLocation/components/LocationHiddenInputs';
import { OnlineCheckbox } from 'hb-react/shared/components/DesignSystem/Inputs/InputLocation/components/OnlineCheckbox';
import {
  type EngineDataLocation,
  type IconWrapperProps,
  type InputAndDropdownTheme,
  type InputLocationProps,
  type InputLocationRef,
  type Location,
} from 'hb-react/shared/components/DesignSystem/Inputs/InputLocation/types';
import { getDistanceFromBounds } from 'hb-react/shared/components/DesignSystem/Inputs/InputLocation/utils/getDistanceFromBounds';
import { validateLocationLevel } from 'hb-react/shared/components/DesignSystem/Inputs/InputLocation/utils/validateLocationLevel';
import useInputAndDropdownTheme from 'hb-react/shared/hooks/theme/useInputAndDropdownTheme';
import * as mobileDeviceFromUserAgent from 'helpers/mobileDeviceFromUserAgent';

import { useLocationSearch } from './hooks/useLocationSearch';
import { useOnlineMode } from './hooks/useOnlineMode';
import InputRadius from './InputRadius';
import { cleanInputValue } from './utils/cleanInputValue';
import { displayChinaLocationVPNToast } from './utils/displayChinaLocationVPNToast';

const CloseIcon = () => (
  <FontAwesomeIcon height="16px" iconName="xmark" iconType={CATEGORIES.SOLID} />
);
const LocationIcon = () => (
  <FontAwesomeIcon height="16px" iconName="location-dot" />
);

const InputLocationNew = ({
  additionalInputs = true,
  ariaDescribedBy = '',
  biasLocationCode,
  className,
  clearable = true,
  defaultLocation = {},
  defaultOnline = false,
  disabled = false,
  icon: IconComponent = LocationIcon,
  id,
  inputAndDropdownTheme: customInputAndDropdownTheme,
  inputHtml,
  inputName,
  label,
  onChange,
  onlineEnabled = false,
  onlineLabel = '',
  onUpdateOnline,
  onSelectLocation,
  placeholder,
  providerName = 'google',
  primaryInputName = 'address',
  radius = {
    enabled: false,
    inputName: '',
  },
  variant = 'normal',
  componentRef,
}: InputLocationProps & {
  componentRef?: React.RefObject<InputLocationRef>;
}) => {
  const locationInputRef = useRef<HTMLInputElement>(null);
  const inputAndDropdownTheme = useInputAndDropdownTheme(
    customInputAndDropdownTheme,
  );
  const theme = useTheme();

  const defaultAddress = defaultLocation?.[primaryInputName];
  const [canReset, setCanReset] = useState(!!defaultAddress);
  const [displayError, setDisplayError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [location, setLocation] = useState<Location>({
    ...defaultLocation,
    address: defaultAddress,
    bias_code: biasLocationCode,
  });

  const prevDefaultLocation = usePrevious(defaultLocation);

  const { isOnlineChecked, updateOnline } = useOnlineMode({
    defaultOnline,
    onUpdateOnline,
    setDisplayError,
    setLocation,
    locationInputRef,
  });

  const provider = window.is_from_china ? 'bing' : providerName;
  const themeInput = theme.input[variant];
  // Coming from SCSS for now (input-with-icon) - to remove once moving to DS select/inputs
  const iconPaddingValue = 40;

  const isClearable = clearable && !disabled && canReset && !isOnlineChecked;
  const aggregatedAriaDescribedBy =
    displayError || ariaDescribedBy
      ? [displayError && `${id}-custom-error`, ariaDescribedBy]
          .filter(Boolean)
          .join(' ')
      : undefined;

  const updateLocationState = useCallback(
    (location: EngineDataLocation) => {
      let callback = onSelectLocation;

      if (!callback) {
        callback = (loc) => {
          const event = new CustomEvent('location:selected', {
            detail: { location: loc },
          });

          document.dispatchEvent(event);
        };
      }

      const distance = getDistanceFromBounds(location.bounds);
      const currentValue = locationInputRef.current?.value || '';

      const cleanedValue = cleanInputValue(currentValue);

      const validLocationLevel = validateLocationLevel(location.location_level);

      const updatedLocation =
        Object.keys(location).length > 0
          ? {
              ...location,
              location: cleanedValue,
              location_level: validLocationLevel,
              distance,
            }
          : {};

      setCanReset(!!location.lat);
      setLocation(updatedLocation);

      // If a location is selected, uncheck the online checkbox
      if (Object.keys(updatedLocation).length > 0) {
        updateOnline(false);
      }

      callback(updatedLocation);

      if (onChange) {
        onChange(updatedLocation);
      }
    },
    [onChange, onSelectLocation, updateOnline],
  );

  const resetLocationState = useCallback(() => {
    setDisplayError(false);
    setCanReset(false);

    // Only call updateLocationState if we're not in online mode
    if (!isOnlineChecked) {
      updateLocationState({});
    }

    if (locationInputRef.current) {
      locationInputRef.current.value = '';
    }
  }, [updateLocationState, isOnlineChecked]);

  useImperativeHandle(componentRef, () => ({
    resetLocationState,
  }));

  // Initialize location search
  useLocationSearch({
    locationInputRef,
    providerName,
    biasLocationCode,
    updateLocation: updateLocationState,
    updateOnline,
    setDisplayError,
    setIsLoading,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;

    if (value === '') {
      resetLocationState();
    } else {
      setCanReset(!!locationInputRef.current?.value);
    }
  };

  const handleOnKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
    }

    setDisplayError(false);
  };

  const handleBlur = () => {
    if (!location.lat && locationInputRef.current?.value) {
      setDisplayError(true);
    }

    // Clean the input value on blur to remove location variations
    if (locationInputRef.current?.value) {
      const currentValue = locationInputRef.current.value;
      const cleanedValue = cleanInputValue(currentValue);

      if (currentValue !== cleanedValue) {
        locationInputRef.current.value = cleanedValue;
      }
    }
  };

  useEffect(() => {
    if (
      prevDefaultLocation?.[primaryInputName]?.length &&
      !defaultLocation?.[primaryInputName]?.length
    ) {
      resetLocationState();
    }
  }, [
    defaultLocation,
    prevDefaultLocation,
    primaryInputName,
    resetLocationState,
  ]);

  useEffect(() => {
    displayChinaLocationVPNToast();
  }, []);

  const defaultValue = location?.address ?? location?.location ?? '';

  return (
    <Wrapper
      className={classNames(className, 'relative')}
      $inputAndDropdownTheme={inputAndDropdownTheme}
    >
      <div
        className={classNames('input-with-icon--left', {
          'input-with-icon': isClearable,
          input: label,
          'input--filled': label && location.lat,
        })}
      >
        {label && <label className="uk-form-label">{label}</label>}

        <Box
          display="flex"
          flexDirection="column"
          alignItems="stretch"
          css={css`
            ${responsive('m')`
              flex-direction: row;
              align-items: center;
            `}
          `}
        >
          <div className="relative" style={{ flex: 1 }}>
            <CustomError $displayError={displayError} id={`${id}-custom-error`}>
              <span aria-hidden>
                {I18n.t('web.users.search.empty_location_error')}
              </span>
            </CustomError>

            {IconComponent && (
              <IconWrapper
                $iconPaddingValue={iconPaddingValue}
                $inputAndDropdownTheme={inputAndDropdownTheme}
                data-testid="input-location-icon-wrapper"
                height={themeInput.iconSize}
                theme={theme}
                width={themeInput.iconSize}
              >
                <IconComponent />
              </IconWrapper>
            )}
            {/* TODO : change to ReactSelectAsyncInput */}
            <StyledInput
              aria-describedby={aggregatedAriaDescribedBy}
              autoComplete="off"
              className={`required uk-input ${inputHtml?.class || ''}`}
              defaultValue={defaultValue}
              disabled={disabled}
              id={id}
              maxLength={255}
              name={`${inputName}[${primaryInputName}]`}
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyUp={handleOnKeyPress}
              placeholder={
                displayError && locationInputRef.current?.value
                  ? ''
                  : placeholder || I18n.t('web.users.search.location')
              }
              ref={locationInputRef}
              type="text"
              theme={theme}
              $inputAndDropdownTheme={inputAndDropdownTheme}
            />

            {isClearable && (
              <IconWrapper
                aria-label={s__('Accessibility|Clean this input value')}
                height={themeInput.iconSize}
                onClick={resetLocationState}
                role="button"
                tabIndex={0}
                theme={theme}
                width={themeInput.iconSize}
                $iconIsClickable
                $iconPaddingValue={iconPaddingValue}
                $inputAndDropdownTheme={inputAndDropdownTheme}
                $rightIcon
              >
                <CloseIcon />
              </IconWrapper>
            )}

            {isLoading && (
              <Box position="absolute" right="35px" bottom="10px">
                <Loader size={16} strokeWidth={2} />
              </Box>
            )}
          </div>

          {onlineEnabled && (
            <OnlineCheckbox
              isOnlineChecked={isOnlineChecked}
              disabled={disabled}
              id={id}
              onlineLabel={onlineLabel}
              inputName={inputName}
              updateOnline={updateOnline}
            />
          )}
        </Box>

        {additionalInputs && (
          <LocationHiddenInputs
            inputName={inputName}
            location={location}
            provider={provider}
          />
        )}
      </div>

      {radius.enabled && !mobileDeviceFromUserAgent.any() && (
        <InputRadius
          originalValue={Math.round(location.distance || 0)}
          inputName={radius.inputName}
          canUpdate={!!(location.lat && location.lng)}
          onUpdateRange={onSelectLocation}
        />
      )}
    </Wrapper>
  );
};

export default InputLocationNew;

const Wrapper = styled.div<{ $inputAndDropdownTheme: InputAndDropdownTheme }>`
  ${({ theme, $inputAndDropdownTheme }) => css`
    & .tt-suggestion {
      color: ${$inputAndDropdownTheme.color} !important;
      background-color: ${$inputAndDropdownTheme.bg as ColorName} !important;
    }

    & .tt-selected {
      color: ${theme.colors.white} !important;
      background-color: ${theme.colors.primary} !important;
    }
  `}
`;

const IconWrapper = styled(Box)<IconWrapperProps>`
  ${focusOutline}

  ${({
    $iconIsClickable,
    $iconPaddingValue,
    $inputAndDropdownTheme,
    $rightIcon,
  }) => css`
    ${$iconIsClickable
      ? `
        cursor: pointer;
        pointer-events: all;
        `
      : `
        cursor: default;
        pointer-events: none;
      `}

    color: ${$inputAndDropdownTheme.iconColor};
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    z-index: 1;
    ${$rightIcon
      ? `
      right: ${$iconPaddingValue / 2}px;
      transform: translate(50%, -50%);
      `
      : `
      left: ${$iconPaddingValue / 2}px;
      transform: translate(-50%, -50%);
      `}
  `}
`;

const StyledInput = styled.input<{
  $inputAndDropdownTheme: InputAndDropdownTheme;
}>`
  ${({ theme, $inputAndDropdownTheme }) => css`
    // To avoid zoom on iOS mobile devices, inputs must have font-size set to 16px on s & m
    font-size: ${theme.fontSizes[3]} !important;
    ${responsive('l')`
      font-size: ${theme.fontSizes[2]} !important;
    `}

    :focus {
      border-color: ${theme.colors.primary} !important;
    }

    padding-top: 0 !important;
    padding-bottom: 0 !important;
    height: 40px !important;
    background: ${$inputAndDropdownTheme.bg} !important;
    color: ${$inputAndDropdownTheme.color} !important;
    border-color: ${$inputAndDropdownTheme.borderColor} !important;

    &::placeholder {
      color: ${$inputAndDropdownTheme.placeholder} !important;
    }
  `}
`;

const CustomError = styled.div<{ $displayError: boolean }>`
  z-index: 0;
  top: 0;
  right: 35px;
  height: 100%;
  opacity: 0;
  color: #be0101;
  display: flex;
  align-items: center;
  pointer-events: none;
  position: absolute;

  ${({ $displayError }) =>
    $displayError &&
    css`
      z-index: 2;
      opacity: 1;
      transition: all 0.5s;
    `}
`;
